export interface nEvent extends Event {
  arguments?: IArguments
  response?: any
}

/**
 * SDK 初始化配置选项
 *
 * @example
 * ```typescript
 * const options: Options = {
 *   region: 'id',
 *   bizType: 'spl',
 *   isLive: true,
 *   autoReportView: true,
 *   autoReportApi: true,
 *   sampleRate: 1,
 * }
 * ```
 */
export type Options = {
  /**
   * 预申请的应用 ID
   */
  appId?: string
  /**
   * 上报地址
   *
   * 不传或传空字符串时，`initUrl()` 会按以下优先级回退：
   * 1. `useWeb: true` → 根据 `isLive` 选生产/测试的 web 上报地址；
   * 2. 否则 → `${window.location.origin}/credit/insight/fe_chat`。
   */
  url?: string
  /**
   * 是否自动上报视图
   *
   * @default true
   */
  autoReportView?: boolean
  /**
   * 是否自动上报 API
   *
   * @default true
   */
  autoReportApi?: boolean
  /**
   * 是否自动上报性能
   *
   * @default true
   */
  autoReportPerf?: boolean
  /**
   * 是否自动上报访问
   *
   * 注意：`autoReportView` 和 `autoReportVisit` 共用同一份页面追踪实现
   * (`createViewAndVisit`)，**任意一个为 true 就会启用整套追踪**。要彻底
   * 关闭 PV / Visit / Stay 自动采集，需同时把两者都设为 false（或使用
   * `manualOnly: true` 一次性关掉所有自动采集）。
   *
   * @default true
   */
  autoReportVisit?: boolean
  /**
   * 是否自动上报 Bridge 调用
   *
   * 关闭后不会包裹 `window.bridgeCallHandler`。
   * @default true
   */
  autoReportBridge?: boolean
  /**
   * 仅手动上报模式
   *
   * 关闭所有自动采集 —— 不包裹 fetch / XHR / history / bridge，也不装 Performance
   * 观察者；但定时上报检查器和 beforeunload flush 仍然启动，保证 `reportCustomInfo`
   * 等手动通道的数据能被发出去。
   *
   * 适合作为下游 SDK 嵌入宿主时使用 —— 宿主已经有自己的全量监控，下游只需要
   * `reportCustomInfo` 把业务自定义指标送到 Insight backend，不希望污染宿主的
   * 全局拦截。
   *
   * **硬覆盖语义**：开启 `manualOnly` 时会把 5 个 `autoReport*` 字段一并置为
   * false，即使同时显式传了 `autoReportApi: true` 也会被覆盖。需要细粒度控制
   * 的调用方应直接使用各 `autoReport*` 字段，不要传 `manualOnly`。
   *
   * @default false
   */
  manualOnly?: boolean
  /**
   * 采样率
   *
   * 取值范围 0-1，1 表示 100% 采样
   * @default 1
   */
  sampleRate?: number
  /**
   * 事件队列最大长度
   *
   * 达到该阈值后立即触发上报；需传入正数，非法值会回退到默认值。
   * @default 20
   */
  maxEventListLength?: number
  /**
   * 解析响应
   *
   * 用于解析 API 响应，提取 code、msg、success 等字段
   */
  parseResponse?: ParseResFn
  /**
   * URL 帮助器
   *
   * 用于转换或规范化 URL，支持字符串、正则表达式、对象或函数
   */
  urlHelper?: (string | RegExp | HelperObj | Function)[]
  /**
   * API 帮助器
   *
   * 用于转换或规范化 API 路径，支持字符串、正则表达式、对象或函数
   */
  apiHelper?: (string | RegExp | HelperObj | Function)[]
  /**
   * 忽略配置
   *
   * 配置需要忽略上报的 URL 或 API
   */
  ignore?: {
    /**
     * 忽略 URL
     *
     * 匹配的 URL 将不会被上报
     */
    ignoreUrls?: (string | RegExp | IgnoreFn)[]
    /**
     * 忽略 API
     *
     * 匹配的 API 将不会被上报
     */
    ignoreApis?: (string | RegExp | IgnoreFn)[]
    // ignoreCode?: (string | number)[]
  }
  /**
   * API 白名单
   *
   * 只有匹配白名单的 API 才会被上报
   */
  whiteListApis?: (string | RegExp | Function)[]
  /**
   * 是否使用 FMP (First Meaningful Paint)
   *
   * @default true
   */
  useFMP?: boolean
  /**
   * 是否使用 FCP (First Contentful Paint)
   *
   * @default true
   */
  useFCP?: boolean
  /**
   * 是否使用 TTI (Time to Interactive)
   *
   * @default true
   */
  useTTI?: boolean
  /**
   * 是否使用 LCP (Largest Contentful Paint)
   *
   * @default true
   */
  useLCP?: boolean
  /**
   * 是否使用 INP (Interaction to Next Paint)
   *
   * @default true
   */
  useINP?: boolean
  /**
   * 性能数据轮询间隔时间（毫秒）
   *
   * 性能指标尚未采集完成时，每隔该时间轮询一次，直到采集完成后上报。
   * @default 600
   */
  perfInterval?: number
  /**
   * 是否自动曝光
   */
  autoExpo?: boolean
  /**
   * 业务类型
   *
   * 用于标识不同的业务线，如 'spl'、'loan' 等
   * @required
   */
  bizType: string
  /**
   * 地区
   *
   * 国家/地区代码，如 'id'、'th'、'ph' 等
   * @required
   */
  region: string
  /**
   * 是否为生产环境
   *
   * @default true
   */
  isLive: boolean
  /**
   * 支持自定义上报请求
   *
   * 可以自定义上报的网络请求实现
   */
  xhr?: (body: any) => any // 支持自定义上报请求
  /**
   * 支持自定义请求头
   *
   * 上报请求时附加的自定义 HTTP 请求头
   */
  headers?: object // 支持自定义请求头
  /**
   * 离线包版本
   *
   * 用于标识当前使用的离线包版本号
   */
  offlineVersion?: string // 离线包版本
  /**
   * 在线包版本
   *
   * 用于标识当前使用的在线包版本号
   */
  onlineVersion?: string // 在线包版本
  /**
   * 获取用户标识
   *
   * 返回当前用户的唯一标识符
   */
  getUser?: () => string // 获取用户标识
  /**
   * 是否使用web上报
   *
   * true: 上报至 web 地址，false: 上报至 origin/credit/insight/fe_chat
   * @deprecated 请使用 useFetch 参数代替
   */
  useWeb?: boolean // 是否使用web上报
  /**
   * 是否使用fetch上报，如果为false，则使用xhr上报
   *
   * @default true 使用fetch上报
   */
  useFetch?: boolean
  /**
   * 实例级会话 ID
   *
   * 每个 Tracer 实例在构造时自动生成，用于区分不同实例产生的数据。
   * 无需手动设置。
   * @internal
   */
  sessionId?: string
  /**
   * 自定义 Prometheus 指标配置
   *
   * 用于配置自定义的 Prometheus 监控指标
   */
  metricConfigs?: MetricConfig[]
}

export type VisitDataType = {
  type: REPORT_TYPE.Visit
  data: {
    url: string
    timestamp: number
    channel?: string
    stay: string | number
  }
}

type ParseResFn = (res: any) => {
  code?: number | string
  msg?: any
  success?: SuccessType
}

enum SuccessType {
  failure = 0,
  success,
}

type IgnoreFn = {
  (arg: string): boolean | undefined
}
type HelperObj = {
  rule: string | RegExp
  target: string
}

export type CommonData = {
  appid: string
  uid: string
  sid: string
  url: string
  connecttype: string
  timestamp: number
  sr: string
  dpr: string
}

export type PerfCommonData = {
  sr: string
  dpr: string
}

export type Perf = {
  dns: number // DNS 解析耗时
  tcp: number // TCP 连接耗时,
  sslready: number // SSL 安全连接耗时
  ttfb: number // Time to First Byte（TTFB），网络请求耗时
  trans: number // 数据传输耗时
  domanalysis: number // DOM 解析耗时
  resload: number // 资源加载耗时
  firstbyte: number // 首包时间
  fpt: number // First Paint Time, 首次渲染时间 / 白屏时间
  tti: number // Time to Interact，首次可交互时间
  domready: number // HTML 加载完成时间， 即 DOM Ready 时间
  loaded: number // 页面完全加载时间
  fmp?: number // 主要有效内容渲染
}

export type PV = {
  page: string // 路径名称
  connecttype?: string // 连接类型
}

export type NavPerfEntry = {
  entryType: string
  initiatorType: string
  timeorigin: number
  domready: number
  loaded: number
}

export type ResPerfEntry = {
  name: string
  initiatorType: string
  duration: number
  transferSize: number
  start: number
  dns: number
  tcp: number
  sslready: number
  reqsent: number
  ttfb: number
  download: number
}

export type RES = {
  nav: NavPerfEntry
  res: ResPerfEntry[]
}

export type ReportData = Perf | PV | RES

export type ExtraPerfData = {
  fmp?: number
}

export type EXTER_PREF = {
  [key: string]: number
}

export interface ExpoParams {
  id: string
}

export interface ExpoConfig {
  root?: HTMLElement
  rootMargin?: string
  threshold?: number
}

export interface ExpoReportData {
  id: string
}

export enum REPORT_TYPE {
  Visit = "visit",
  View = "pv",
  Api = "api",
  Click = "click",
  Impression = "impression",
  Perf = "perf",
  Stay = "stay",
  Bridge = "bridge",
  Error = "error",
  Custom = "custom",
}

export enum ActionType {
  EXPO = "expo",
  CLICK = "click",
}

export interface DataExpoAttrParams {
  actionId: string
  params?: ExpoParams
}

export interface EventDataType {
  timestamp?: number
  url?: string
  type: string
  fromUrl?: string
  data: ReportDataType[keyof ReportDataType]
  metricData?: ReportCustomMetricParams[]
}

export interface ReportDataType {
  pv: {
    channel: string
  } // channel
  stay: {
    stay: number
  } // 页面停留时间
  // click: string // 点击元素名
  // impression: string // 曝光元素名
  api: {
    path: string // api 的 path
    body?: any // 请求体
    res?: any // 响应数据
    duration: number // 请求端对端耗时
    requestId: string // 请求唯一标识
    timestamp: number // 请求响应时间戳
  }
  // TODO:
  perf: {}
  bridge: {
    name: string
    duration?: number
    type: number
    isSuccess?: boolean
    successResult?: number
    [property: string]: any
  }
  error: {
    error: string
    extra: object
  }
  custom: {
    message: string
    extra: object
    metricConfigs?: MetricConfig[]
  }
  [property: string]: any
}

export interface bridgeDetailParams {
  name: string
  duration?: number
  type?: number
  isSuccess: boolean
  successResult?: number
}

export enum BridgeTypeEnum {
  total = 1, // 总调用情况
  detail,
  custom, // 业务自定义上报
}

export interface ReportReqParams {
  header: CommonHeader
  message: ReportMessage
}

export interface CommonHeader {
  /**
   * 设备品牌，如 iphone / huawei
   */
  device_brand: string

  /**
   * 操作系统，如 Android / iOS
   */
  system_type: string

  /**
   * 操作系统版本
   */
  system_version: string

  /**
   * shopee app 版本
   */
  app_version: string

  rn_version: string

  /**
   * 网络类型：wifi / 4g / ...
   */
  network_type: NetworkTypeEnum | string

  /**
   * credit h5 应用周期内生成的唯一 uuid
   */
  session_id: string

  /**
   * 当前业务名。本次上报来自哪个业务？ 自定义
   */
  biz_type: string

  /**
   * 当前国家/地区名。本次上报来自哪个国家/地区？ 如： ID,TH,PH,TW,MY,SG,VN
   */
  region: string

  /**
   * 离线包版本
   */
  offline_pkg_version: string
}

export interface ReportMessage {
  cmd: string
  eventList?: EventDataType[]
  pageHistory?: PageHistory[]
}

export interface PageHistory {
  url?: string
  timestamp?: number
  channel: string
  stay: string | number
  type: string
  fromUrl?: string
}

export interface FinalRequestParams {
  headers: Headers
  body: ReportReqParams
}

export interface Headers {
  Accept: string
  "Content-Type": string
  "X-Kredit-Req-APPID": string
  "X-Kredit-Req-H": string
  "X-Kredit-Req-Source": number
  "X-Kredit-Trace-ID": string
  "X-Kredit-Start-Timestamp": string
  "X-Kredit-Req-Language": string
}

export enum CMD {
  REPORT = "report",
}

// api上报参数
export interface ApiParamsType {
  path: string
  duration: number
  httpStatus: number
  body?: any
  res?: any
  [property: string]: any
}
export enum NetworkTypeEnum {
  "unknown" = 0,
  "slow-2g",
  "2g",
  "3g",
  "4g",
  "wifi" = 9,
}

export type MetricType = "counter" | "histogram" | "summary"

export interface MetricConfig {
  metricName: string
  type: MetricType
  labels: string[]
  buckets?: number[]
  percentiles?: number[]
  desc: string
}

/**
 * 从 MetricConfig 数组中提取所有 metricName 的联合类型
 *
 * @example
 * ```typescript
 * type Names = MetricNameOf<[
 *   { metricName: 'page_load'; type: 'histogram'; labels: []; desc: '' },
 *   { metricName: 'api_error'; type: 'counter'; labels: []; desc: '' },
 * ]>
 * // => 'page_load' | 'api_error'
 * ```
 */
export type MetricNameOf<T extends MetricConfig[]> = T[number]["metricName"]

export interface ReportCustomMetricParams {
  metricName: string
  value?: number
}
